import { useEffect, useRef } from "react";
import { usePrecisionGameLeaderboardStore } from "../store/precisionGameLeaderboard.store";
import { collectionSubscriptions } from "@/api/collectionRef/collectionOperations";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { PrecisionGameLeaderboard } from "../models/precisionGame.types";

export const usePrecisionGameLeaderboard = (collection: string) => {
  const {
    records,
    winners,
    mentions,
    loading,
    error,
    setRecords,
    setWinners,
    setMentions,
    setLoading,
    setError,
  } = usePrecisionGameLeaderboardStore();

  const unsubscribeRef = useRef<(() => void) | null>(null);

  const processData = (data: any[]) => {
    const recordsArray: PrecisionGameLeaderboard[] = [];
    const winnersArray: PrecisionGameLeaderboard[] = [];
    const honorableMentionsArray: PrecisionGameLeaderboard[] = [];

    data.forEach((item) => {
      const processedItem = item as PrecisionGameLeaderboard;
      recordsArray.push(processedItem);

      if (processedItem.score === 1337) {
        winnersArray.push(processedItem);
      } else {
        honorableMentionsArray.push(processedItem);
      }
    });

    setWinners(winnersArray);
    setMentions(honorableMentionsArray);
    setRecords(recordsArray);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);

    try {
      // Subscribe to real-time updates
      unsubscribeRef.current = collectionSubscriptions.precisionGame(
        brand.tenantId,
        collection,
        (data) => {
          const enhancedData = data.map((item, index) => ({
            id: item.id || `item-${index}`,
            ...item,
          }));
          processData(enhancedData);
        }
      );
    } catch (err) {
      setError(err);
      setLoading(false);
    }

    // Clean up subscription when component unmounts
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [collection]); // Re-subscribe when collection changes

  const refresh = () => {
    // Unsubscribe from the current listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    setLoading(true);

    // Re-subscribe to get fresh data
    try {
      unsubscribeRef.current = collectionSubscriptions.precisionGame(
        brand.tenantId,
        collection,
        (data) => {
          const enhancedData = data.map((item, index) => ({
            id: item.id || `item-${index}`,
            ...item,
          }));
          processData(enhancedData);
        }
      );
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  return {
    data: records,
    winners,
    mentions,
    loading,
    error,
    refetch: refresh,
  };
};
